import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Button,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tabs,
  Tab,
  Switch,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import FileDownload from '@mui/icons-material/FileDownload';
import { useDeleteUsersByIdMutation, useLazyExportUsersQuery, useLazyGetUserListQuery, useUpdateUsersByIdMutation } from "../../services/user";
import { showError } from "../../constants/toasts";
import Pagination from "../../components/Panigation";
import DeleteConfirmationModal from "../../components/DeleteModal";
import { showToast } from "../../helpers";
import SearchBar from "../../components/SearchBar";
import { isValidInput } from "../../utils/validations";
import { USER_TYPE } from "../../constants/enums";
import { USE_FAKE_DATA } from "../../config/appConfig";

export interface UserListResponse {
  _id: string;
  fullName: string;
  email: string;
  phoneNo: string;
  dialCode: string;
  memberAccount: string;
  dob: string;
}

const FAKE_USERS: Array<{
  _id: string;
  fullName: string;
  email: string;
  phoneNo: string;
  dialCode: string;
  memberAccount: string;
  dateOfBirth?: string;
  userType?: number;
  isBlocked?: boolean;
  requestStatus?: 'PENDING' | 'ACCEPTED' | 'REJECTED';
  accountName?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  customerType?: 'CASH' | 'CREDIT' | '';
  isLocked?: boolean;
}> = [
  {
    _id: "u1",
    fullName: "John Doe",
    companyName: "Apple",
    email: "john.doe@example.com",
    phoneNo: "5551234567",
    dialCode: "+1",
    memberAccount: "M-100001",
    dateOfBirth: "1990-05-21",
    userType: USER_TYPE.APP,
    isBlocked: false,
    requestStatus: 'PENDING',
    customerType: '',
    isLocked: false,
  },
  {
    _id: "u2",
    fullName: "Jane Smith",
    companyName: "Facebook",
    email: "jane.smith@example.com",
    phoneNo: "971501234567",
    dialCode: "+971",
    memberAccount: "M-100002",
    dateOfBirth: "1988-11-02",
    userType: USER_TYPE.ERP,
    isBlocked: true,
    requestStatus: 'PENDING',
    customerType: '',
    isLocked: false,
  },
  {
    _id: "u3",
    fullName: "Alex Johnson",
    companyName: "Apple",
    email: "alex.j@example.com",
    phoneNo: "447911123456",
    dialCode: "+44",
    memberAccount: "M-100003",
    dateOfBirth: "1995-01-15",
    userType: USER_TYPE.APP,
    isBlocked: false,
    requestStatus: 'ACCEPTED',
    customerType: 'CASH',
    isLocked: false,
  },
  {
    _id: "u4",
    fullName: "Priya Kumar",
    companyName: "Google",
    email: "priya.k@example.com",
    phoneNo: "919876543210",
    dialCode: "+91",
    memberAccount: "M-100004",
    dateOfBirth: "1992-07-07",
    userType: USER_TYPE.ERP,
    isBlocked: false,
    requestStatus: 'ACCEPTED',
    customerType: 'CREDIT',
    isLocked: false,
  },
  {
    _id: "u5",
    fullName: "Mohamed Ali",
    companyName: "Microsoft",
    email: "m.ali@example.com",
    phoneNo: "201001234567",
    dialCode: "+20",
    memberAccount: "M-100005",
    dateOfBirth: "1985-03-30",
    userType: USER_TYPE.APP,
    isBlocked: true,
    requestStatus: 'PENDING',
    customerType: '',
    isLocked: false,
  },
];

const UserList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [openDelete, setOpenDelete] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [status, setStatus] = useState<string>("");
  const [userType, setUserType] = useState<string>("");
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [UsersData, setUsersData] = useState<any[]>([]);
  const [statusTab, setStatusTab] = useState<'PENDING' | 'ACCEPTED' | 'REJECTED'>("PENDING");
  const [fakeUsers, setFakeUsers] = useState<any[]>(FAKE_USERS);

  const handleStatusChange = (event: SelectChangeEvent) => {
    setStatus(event.target.value as string);
  };

  const handleLockChange = (id: string, nextLocked: boolean) => {
    if (!USE_FAKE_DATA) return;
    setFakeUsers((prev) => prev.map((u) => (u._id === id ? { ...u, isLocked: nextLocked } : u)));
  };

  const handleActiveChange = (id: string, nextActive: boolean) => {
    if (!USE_FAKE_DATA) return;
    // Active maps to !isBlocked
    setFakeUsers((prev) => prev.map((u) => (u._id === id ? { ...u, isBlocked: !nextActive } : u)));
  };

  const handleUserTypeChange = (event: SelectChangeEvent) => {
    setUserType(event.target.value as string);
  };

  const handleCustomerTypeChange = (id: string, value: 'CASH' | 'CREDIT' | '') => {
    if (!USE_FAKE_DATA) return;
    setFakeUsers((prev) =>
      prev.map((u) => (u._id === id ? { ...u, customerType: (value || undefined) as any } : u))
    );
  };

  const handleDecisionChange = (id: string, action: 'ACCEPT' | 'REJECT') => {
    if (!USE_FAKE_DATA) return;
    const target = fakeUsers.find((u) => u._id === id);
    if (action === 'ACCEPT' && (!target || !target.customerType)) {
      showError('Please select a customer type before accepting');
      return;
    }
    setFakeUsers((prev) =>
      prev.map((u) =>
        u._id === id
          ? { ...u, requestStatus: action === 'ACCEPT' ? 'ACCEPTED' : 'REJECTED' }
          : u
      )
    );
  };

  const [getUserList] = useLazyGetUserListQuery();
  const [exportUsers] = useLazyExportUsersQuery();
  const [deleteUser] = useDeleteUsersByIdMutation();
  const [updateUser] = useUpdateUsersByIdMutation();

  const toggleDeleteModal = () => {
    setOpenDelete(!openDelete);
  }

  const handleDelete = async () => {
    try {
      await deleteUser({ id: activeId }).unwrap();
      toggleDeleteModal();
      fetchList();
      showToast('User deleted successfuly')
    } catch (e) {
      console.log(e)
    }
  };

  const handleToggle = async (promo: any, nextChecked: boolean) => {
    const id = promo?._id || promo?.id;
    if (!id) return;
    const nextIsBlocked = nextChecked;
    try {
      await updateUser({ body: { isBlocked: nextIsBlocked } as any, id }).unwrap();
      showToast(`User ${nextChecked ? 'Disabled' : 'Activated'}`)
      fetchList();
    } catch (e) {
      console.log(e)
    }
  };

  const fetchList = async () => {
    try {
      if (USE_FAKE_DATA) {
        let data = [...fakeUsers];

        // Filter by selected tab (Pending / Accepted / Rejected)
        data = data.filter((u) => u.requestStatus === statusTab);

        const term = (debouncedSearchTerm || "").toLowerCase();
        if (term) {
          data = data.filter((u) =>
            u.fullName.toLowerCase().includes(term) ||
            (u.email || "").toLowerCase().includes(term) ||
            (u.phoneNo || "").toLowerCase().includes(term)
          );
        }

        if (status !== "" && status !== "0") {
          if (status === "1") data = data.filter((u) => !u.isBlocked);
          else if (status === "2") data = data.filter((u) => !!u.isBlocked);
        }

        if (userType && userType !== "0") {
          if (userType === "1") data = data.filter((u) => (u.customerType || "") === 'CASH');
          else if (userType === "2") data = data.filter((u) => (u.customerType || "") === 'CREDIT');
        }

        const total = data.length;
        const start = (page - 1) * pageSize;
        const end = start + pageSize;
        const paged = data.slice(start, end);

        setUsersData(paged);
        setTotalCount(total);
        return;
      }

      const params: any = {
        page: page,
        limit: pageSize,
        search: debouncedSearchTerm,
        // map customerType filter when selected
      };

      if (status === "1") params.isBlocked = false;
      else if (status === "2") params.isBlocked = true;

      if (userType === "1") params.customerType = 'CASH';
      else if (userType === "2") params.customerType = 'CREDIT';

      const response = await getUserList(params).unwrap();

      if (response?.statusCode === 200) {
        setUsersData(response?.data?.list || []);
        setTotalCount(response?.data?.totalCount || 0);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to fetch Users");
    }
  };

  const exportUsersCall = async () => {
    try {
      const response = await exportUsers({}).unwrap();
      if (response?.statusCode === 200) {
        if (response?.data) {
          window.open(response?.data, '_blank');
        }
        showToast('Users exported successfully');
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to export Users");
    }
  };

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / (pageSize || 10)));

  useEffect(() => {
    fetchList();
  }, [debouncedSearchTerm, page, pageSize, status, userType, statusTab, fakeUsers]); // include status tab and fake data changes

  return (
    <>
      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%", mb: 1 }}>
            <Tabs
              value={statusTab === 'PENDING' ? 0 : statusTab === 'ACCEPTED' ? 1 : 2}
              onChange={(_, idx: number) => setStatusTab(idx === 0 ? 'PENDING' : idx === 1 ? 'ACCEPTED' : 'REJECTED')}
              className="custom_tabs_links"
            >
              <Tab label="Pending" />
              <Tab label="Accepted" />
              <Tab label="Rejected" />
            </Tabs>
          </Box>
          <SearchBar
            searchTerm={searchTerm}
            placeholder="Search..."
            setDebouncedSearchTerm={setDebouncedSearchTerm}
            value={searchTerm}
            onCross={() => setSearchTerm('')}
            onChange={(val: any) => {
              if (isValidInput(val.target.value)) {
                setSearchTerm(val.target.value);
              }
            }}
          />
          <div className=" form">
            <Select
              fullWidth
              value={status}
              displayEmpty
              onChange={handleStatusChange}>
              <MenuItem disabled value="">Select Status</MenuItem>
              <MenuItem value="0">All</MenuItem>
              <MenuItem value="1">Active</MenuItem>
              <MenuItem value="2">Deactivated</MenuItem>
            </Select>
          </div>
          <div className=" form">
            <Select
              fullWidth
              value={userType}
              onChange={handleUserTypeChange}
              displayEmpty
            >
              <MenuItem disabled value="">Customer Type</MenuItem>
              <MenuItem value="0">All</MenuItem>
              <MenuItem value="1">Cash</MenuItem>
              <MenuItem value="2">Credit</MenuItem>
            </Select>
          </div>
        </div>
        <div className="right_s">
          {/* <Button onClick={() => navigate('/manage-users/add')} className="btn btn_primary"><AddIcon /> Add User</Button> */}
          <Button onClick={exportUsersCall} className="btn btn_primary"><FileDownload /> Export User</Button>
        </div>
      </div>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Account Name</TableCell>
              <TableCell>First Name</TableCell>
              <TableCell>Last Name</TableCell>
              <TableCell>Company Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Customer Type</TableCell>
              {statusTab !== 'PENDING' && <TableCell>Lock</TableCell>}
              {statusTab !== 'PENDING' && <TableCell>Status</TableCell>}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>

            {
              UsersData.length ?
                UsersData?.map((user) => {
                  const firstName = user?.firstName || (user?.fullName || '').split(' ')[0] || '';
                  const lastName = user?.lastName || (user?.fullName || '').split(' ').slice(1).join(' ') || '';
                  return (
                  <TableRow hover key={user?._id}>
                    <TableCell>
                      <Box
                        component="a"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/manage-users/detail/${user?._id || ''}`, { state: { user } });
                        }}
                        className="user_block click_action"
                      >
                        <p>
                          <b>{user?.accountName || user?.fullName || '-'}</b>
                        </p>
                      </Box>
                    </TableCell>
                    <TableCell>{firstName || '-'}</TableCell>
                    <TableCell>{lastName || '-'}</TableCell>
                    <TableCell>{user?.companyName || '-'}</TableCell>
                    <TableCell>{`${user?.dialCode || ''} ${user?.phoneNo || ''}`}</TableCell>
                    <TableCell>{user?.email || ''}</TableCell>
                    <TableCell>
                      {USE_FAKE_DATA ? (
                        <Select
                          size="small"
                          displayEmpty
                          value={(user?.customerType as any) || ''}
                          onChange={(e: any) =>
                            handleCustomerTypeChange(user?._id, (e.target.value as 'CASH' | 'CREDIT' | ''))
                          }
                          sx={{
                            minWidth: 140,
                            '& .MuiSelect-select, & .MuiOutlinedInput-input': {
                              fontSize: 13,
                              fontWeight: 400,
                              py: '4px',
                            },
                          }}
                        >
                          <MenuItem value="" disabled>Select Type</MenuItem>
                          <MenuItem value="CASH">Cash</MenuItem>
                          <MenuItem value="CREDIT">Credit</MenuItem>
                        </Select>
                      ) : (
                        user?.customerType === 'CASH' ? 'Cash' : user?.customerType === 'CREDIT' ? 'Credit' : '-'
                      )}
                    </TableCell>
                    {statusTab !== 'PENDING' && (
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Switch
                            size="small"
                            checked={!!user?.isLocked}
                            onChange={(_, checked) => handleLockChange(user?._id, checked)}
                            />
                          </Box>
                      </TableCell>
                    )}
                    {statusTab !== 'PENDING' && (
                      <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Switch
                              size="small"
                              checked={!user?.isBlocked}
                              onChange={(_, checked) => handleActiveChange(user?._id, checked)}
                            />
                          </Box>
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="table_action_wrapper">
                        <div className="table_actions v2">
                          {USE_FAKE_DATA && (
                            <Select
                              size="small"
                              displayEmpty
                              value={
                                statusTab === 'PENDING'
                                  ? ''
                                  : user?.requestStatus === 'ACCEPTED'
                                  ? 'ACCEPT'
                                  : user?.requestStatus === 'REJECTED'
                                  ? 'REJECT'
                                  : ''
                              }
                              onChange={(e: any) =>
                                handleDecisionChange(
                                  user?._id,
                                  e.target.value === 'ACCEPT' ? 'ACCEPT' : 'REJECT'
                                )
                              }
                              sx={{
                                mr: 1,
                                minWidth: 120,
                                '& .MuiSelect-select': {
                                  fontSize: 13,
                                  fontWeight: 400,
                                  paddingTop: '4px',
                                  paddingBottom: '4px',
                                },
                                '& .MuiOutlinedInput-input': {
                                  fontSize: 13,
                                  fontWeight: 400,
                                  paddingTop: '4px',
                                  paddingBottom: '4px',
                                },
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    '& .MuiMenuItem-root': {
                                      fontSize: 13,
                                      fontWeight: 400,
                                      py: 0.5,
                                    },
                                  },
                                },
                              }}
                            >
                              <MenuItem value="" disabled>
                                Approvals
                              </MenuItem>
                              <MenuItem value="ACCEPT">Accept</MenuItem>
                              <MenuItem value="REJECT">Reject</MenuItem>
                            </Select>
                          )}
                          <IconButton
                            onClick={() =>
                              navigate(`/manage-users/detail/${user?._id || ''}`, { state: { user } })
                            }
                          >
                            <img src="/static/images/eye_icon.svg" alt="View" />
                          </IconButton>
                          <IconButton
                            onClick={() =>
                              navigate(`/manage-users/edit/${user?._id || ''}`, { state: { user } })
                            }
                          >
                            <img src="/static/images/edit2_icon.svg" alt="Edit" />
                          </IconButton>
                          {/* <IconButton onClick={() => {
                            setActiveId(user?._id || '');
                            toggleDeleteModal();
                          }}>
                            <img src="/static/images/trash2_icon.svg" alt="Delete" />
                          </IconButton> */}
                        </div>
                        {/* <Button
                          className="btn btn_primary sm"
                          onClick={() => setOpenModal(true)}
                        >
                          Verification
                        </Button> */}
                      </div>
                    </TableCell>
                  </TableRow>
                  )
                }) :
                <TableRow>
                  <TableCell colSpan={statusTab === 'PENDING' ? 8 : 10}>No Data Found</TableCell>
                </TableRow>}
          </TableBody>
        </Table>
        <Pagination
          module={UsersData || []}
          page={page}
          setPage={setPage}
          limit={pageSize}
          setLimit={setPageSize}
          onPageChange={onPageChange}
          totalPages={totalPages}
        />
      </TableContainer>

      {/* <VerificationModal
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
      /> */}
      <DeleteConfirmationModal
        open={openDelete}
        onClose={toggleDeleteModal}
        handleDelete={handleDelete}
        header="Are you sure you want to delete this user?"
      />
    </>
  );
};

export default UserList;
